// C.2 — input reducers for POST /api/financials/compute (the case/CC
// financial summaries + success fee, relocated server-side; recording:
// recordings/c2-financials/). Every call site posts the SAME reduced shapes
// so the wire domain stays closed: absent fields are normalized to null
// (never undefined — JSON would drop the key and the verbatim server
// arithmetic distinguishes null from missing), row order is preserved
// (float summation order is load-bearing), and rows are reduced to exactly
// the compute read-set the browser closures used to read.
//
// See server/src/lib/caseFinancials.js for the shapes' consumers.

// 182 D.5 (5) — typed: what goes in comes out, `undefined` alone becomes
// `null` (null stays null). Callers pass fields of `any` rows and infer `any`
// for T, so every one of them compiles unchanged. A function declaration, not
// a `<T>(v) =>` arrow: the i18n-total detector parses this file with JSX on
// and read `<T>` as a tag — 48 "jsx-text" literals out of the comments below.
function nn<T>(v: T | undefined): T | null {
  return v === undefined ? null : v;
}
// 169 R7 — a row's currency rides the wire ONLY when present: `{ currency }`
// or nothing, so `undefined` drops from JSON and every frozen recording input
// (none carries a currency) is unchanged. Read through `unknown`, never off
// the `any` row, so the reducers below pay no unsafe-access warning for it.
const currencyOf = (x: unknown): { currency: string } | Record<string, never> => {
  const c = x && typeof x === "object" ? (x as { currency?: unknown }).currency : undefined;
  return typeof c === "string" ? { currency: c } : {};
};

// BRIEF 87 §1b — THE FREEZE READ-SET, carried by every entry reducer below.
//
// An invoiced entry's price is no longer derivable from the case: once its pro
// forma has been sent, `TimeEntry.invoicedRate` / `.invoicedAmount` hold what
// the client was actually told, and the five server compute libs prefer them
// over `referenceHourlyRate × ratio`. They cannot prefer what they are not
// sent — these endpoints are pure reducers over rows the BROWSER posts and
// consult no database ("inline client-held rows in, raw doubles out … DB not
// consulted") — so the three fields have to travel on the wire.
//
// `invoiceId` is one of the three because the applicability rule needs it:
// cancelling a pro forma clears `invoiceId` and KEEPS the frozen columns, so a
// released entry must price live again and only the pair says so.
//
// Declared once and spread, rather than typed out seven times, because seven
// copies is how one of them silently stops carrying a field — which is the
// whole finding of brief 59 and the reason `redMatterFin`'s siblings exist at
// all. For a financially-gated viewer all three arrive already null from the
// API (they are in TIME_ENTRY_FINANCIAL_FIELDS), so posting them back yields
// the same zeros that viewer's browser has always computed.
type FreezeRead = {
  invoiceId?: number | null;
  invoicedRate?: number | null;
  invoicedAmount?: number | null;
};
const frz = (e: FreezeRead) => ({
  invoiceId: nn(e.invoiceId),
  invoicedRate: nn(e.invoicedRate),
  invoicedAmount: nn(e.invoicedAmount),
});

// BRIEF 87 §3b — Invoice → the per-entry VAT read-set, and nothing else.
//
// `POST /financials/compute` resolves FirmSettings.vatRate live and applied it
// to entries of any age, so a firm VAT change re-priced every historical
// figure. Belgian VAT is the rate in force at the time of supply, so an entry
// under a SENT invoice must take that invoice's own stored rate — which already
// exists as `Invoice.vatRate`, a real snapshot column, needing no migration.
//
// Two fields, because two is all the server reads. The endpoint treats the whole
// input as OPTIONAL: absent, every lookup misses and the firm rate applies to
// everything, which is exactly the behaviour that was there before.
export const redFinInv = (i: { id?: number | null; vatRate?: number | null }) => ({
  id: nn(i.id),
  vatRate: nn(i.vatRate),
});

// Case / CollectiveCase → the block/metrics/panel/modal read-set.
export const redMatterFin = (c: any) => ({
  id: nn(c.id),
  referenceHourlyRate: nn(c.referenceHourlyRate),
  costsRatio: nn(c.costsRatio),
  claimAmount: nn(c.claimAmount),
  successFeeRate: nn(c.successFeeRate),
  caseCollaborators: (c.caseCollaborators || []).map((x: any) => ({
    coId: nn(x.coId),
    ratio: nn(x.ratio),
  })),
  // 169 R7 — a payment's currency rides the wire ONLY when present, so
  // `undefined` drops from JSON and every frozen recording input is unchanged.
  // 199 §F — AND ITS DATE AND ITS DOCUMENT: the recap's costs line (costsLine,
  // caseFinancials.js) counts a payment only when dated, and takes its ex-VAT
  // share from the document it answers (`invoiceId`, 198 A.3a's rule) — the
  // two fields redCostsMatter already posts. The server's mirror
  // (matterRecap.js) carries both, field for field; a frozen input lacking
  // them reads as before (no date → not counted).
  payments: (c.payments || []).map((p: { amount?: number | null; date?: string | null; invoiceId?: number | null; currency?: string | null }) => ({
    amount: nn(p.amount), date: nn(p.date), invoiceId: nn(p.invoiceId), ...currencyOf(p),
  })),
});

// TimeEntry → both matter keys + the money fields (posted in te-array order).
// 161 §8 — `mark` joins the read-set: the recap splits uninvoiced rows by
// billability mark (the strip's split), not by the legacy `billed` flag.
export const redEntryFin = (e: any) => ({
  caId: nn(e.caId),
  ccId: nn(e.ccId),
  coId: nn(e.coId),
  min: nn(e.min),
  billed: nn(e.billed),
  date: nn(e.date),
  mark: nn(e.mark),
  ...frz(e),
  // 199 §F — the activity code: the recap's activity line (activityBlock,
  // caseFinancials.js) groups the matter's minutes by it, as redCostsEntry
  // already posts it. Absent on a frozen input → no cell, as before.
  type: nn(e.type),
});

// 161 §8 — Invoice → the recap's DOCUMENT-basis read-set (R7). The served
// row already carries every field (amountDue is the P17.1c authority,
// unconsumed the §8 remainder); this reducer just keeps the wire domain
// closed like its siblings. 161A §4 [R14] widens it with the strip's
// invoiceLevel read-set — credits, the payments array and the two served
// allocation figures — so the Costs & Fees paid/due columns can run the
// strip's own formulas "on the invoices concerned".
export const redRecapInv = (i: {
  id?: number | null; caseId?: number | null; ccId?: number | null;
  docKind?: string | null; subtotalFees?: number | null;
  subtotalCosts?: number | null; totalAmount?: number | null;
  vatRate?: number | null; sentToClientAt?: string | null;
  sentToPeppolAt?: string | null; everSentAt?: string | null; issueDate?: string | null;
  paymentStatus?: string | null;
  deletedAt?: string | null; amountDue?: number | null;
  unconsumed?: number | null;
  commercialDeduction?: number | null;
  credits?: Array<{ amount?: number | null }> | null;
  payments?: Array<{ amount?: number | null }> | null;
  paidAllocated?: number | null; paidHeld?: number | null;
  currency?: string | null;
  subtotalExVAT?: number | null; vatAmount?: number | null;
}) => ({
  id: nn(i.id),
  caseId: nn(i.caseId),
  ccId: nn(i.ccId),
  docKind: nn(i.docKind),
  subtotalFees: nn(i.subtotalFees),
  subtotalCosts: nn(i.subtotalCosts),
  totalAmount: nn(i.totalAmount),
  vatRate: nn(i.vatRate),
  // 198 §A.3 — THE DOCUMENT'S OWN EX-VAT / VAT SPLIT, the two stored columns the ladder
  // persists (routes/invoices.js; 191 E.2a's `totalInclVAT` is their sum). « TAUX HORAIRE
  // EFFECTIF » takes a payment's ex-VAT share as amount × subtotalExVAT / (subtotalExVAT +
  // vatAmount) of the document it answers — the split as issued, whatever the rate mix —
  // and a document storing neither answers no share at all (null, reported), never 1/1.21.
  // The server's mirror (lib/matterRecap.js redRecapInv) carries both, field for field.
  subtotalExVAT: nn(i.subtotalExVAT),
  vatAmount: nn(i.vatAmount),
  sentToClientAt: nn(i.sentToClientAt),
  sentToPeppolAt: nn(i.sentToPeppolAt),
  // 191 §G — the send date (179 C.1a's one-shot stamp) and the issue date ride
  // the read-set: Frais et honoraires' period window reads them in recapBlock
  // (the server's mirror, matterRecap.js, carries both field for field).
  everSentAt: nn(i.everSentAt),
  issueDate: nn(i.issueDate),
  paymentStatus: nn(i.paymentStatus),
  deletedAt: nn(i.deletedAt),
  amountDue: nn(i.amountDue),
  unconsumed: nn(i.unconsumed),
  // 190 round 8 [AI] — the margin nets the commercial discount (caseFinancials
  // recapBlock); the server's mirror (matterRecap.js redRecapInv) carries it too.
  commercialDeduction: nn(i.commercialDeduction),
  credits: Array.isArray(i.credits) ? i.credits.map((c) => ({ amount: nn(c && c.amount) })) : [],
  payments: Array.isArray(i.payments) ? i.payments.map((p) => ({ amount: nn(p && p.amount) })) : [],
  paidAllocated: nn(i.paidAllocated),
  paidHeld: nn(i.paidHeld),
  // 169 R7 — the document's currency, only when present (frozen inputs unchanged).
  ...(i.currency != null ? { currency: i.currency } : {}),
});

// Collaborator → the effR chain + labor-cost read-set. (For non-admin
// viewers defaultRatio/internalRates arrive scrubbed to null from the API —
// posting them back yields the same zeros their browser used to compute.)
export const redCoFin = (c: {
  id?: number | null; role?: string | null; isFeeEarner?: boolean | null; billsAtZero?: boolean | null;
  defaultRatio?: number | null; roleDefaultRatio?: number | null; internalRates?: unknown;
}) => ({
  id: nn(c.id),
  role: nn(c.role),
  // 199 §F — the fee-earner flag: costsLine's « % par associé » (B.2, hours of
  // the fee-earning roles) reads `isFeeEarner` before the role-name fallback,
  // as redCostsCo already posts it. Absent on a frozen input → the fallback.
  isFeeEarner: nn(c.isFeeEarner),
  billsAtZero: nn(c.billsAtZero),
  defaultRatio: nn(c.defaultRatio),
  roleDefaultRatio: nn(c.roleDefaultRatio),
  internalRates: nn(c.internalRates),
});

// useRoleLabels rows → the roleDefaultRatio() read-set.
export const redRoleRows = (rows: any[]) =>
  (rows || []).map((r: any) => ({ name: nn(r.name), defaultRatio: nn(r.defaultRatio) }));

// --- C.2 (reports aggregates) — POST /api/financials/reports reducers -------
// (recording: recordings/c2-reports/; server consumer:
// server/src/lib/reportsAggregates.js). Same wire discipline as above; the
// builders ECHO several input fields into their output rows (ref/title/
// status/ctype, names, invoice metadata), so the reducers carry the echo set
// alongside the compute read-set.

// Case / CollectiveCase → the byCase/byCollab/byPeriod/byClient read-set
// (cc rows carry no clIds on the wire — nn() posts null, the builders'
// `|| []` reproduces the browser's fallback).
// 191 F.0 — the seven and the per-code right three, numbers or null; nothing
// else rides (the recap's other members never leave the server).
const redRecapMember = (r: any) => {
  if (!r || typeof r !== "object") return null;
  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
  const out: Record<string, unknown> = {};
  for (const m of ["unmarked", "unbillable", "billable", "invoiced", "sent", "paid", "due"]) out[m] = num(r[m]);
  if (r.foreign && typeof r.foreign === "object") {
    const f: Record<string, { sent: number | null; paid: number | null; due: number | null }> = {};
    for (const [code, b] of Object.entries(r.foreign as Record<string, any>)) {
      f[code] = { sent: num(b?.sent), paid: num(b?.paid), due: num(b?.due) };
    }
    if (Object.keys(f).length) out.foreign = f;
  }
  return out;
};
export const redRepMatter = (c: any) => ({
  id: nn(c.id),
  ref: nn(c.ref),
  title: nn(c.title),
  status: nn(c.status),
  ctype: nn(c.ctype),
  costsRatio: nn(c.costsRatio),
  referenceHourlyRate: nn(c.referenceHourlyRate),
  clIds: nn(c.clIds),
  caseCollaborators: (c.caseCollaborators || []).map((x: any) => ({ coId: nn(x.coId), ratio: nn(x.ratio) })),
  // 191 F.0 — the matter's served recap (lib/matterRecap.js recapWire, the
  // list row's own seven), posted as served so Par dossier / Par client READ
  // the member (reportsAggregates.js) and compute none of it; null when the
  // row carries none (not a financial viewer of that matter).
  recap: redRecapMember(c.recap),
});

// TimeEntry → both matter keys + the money fields + the billability mark
// (posted in te-array order — float summation order is load-bearing).
export const redRepEntry = (e: any) => ({
  caId: nn(e.caId),
  ccId: nn(e.ccId),
  coId: nn(e.coId),
  min: nn(e.min),
  billed: nn(e.billed),
  date: nn(e.date),
  mark: nn(e.mark),
  ...frz(e),
});

// Collaborator → the effR chain + the pname/role/email echo set.
export const redRepCo = (c: any) => ({
  id: nn(c.id),
  role: nn(c.role),
  billsAtZero: nn(c.billsAtZero),
  defaultRatio: nn(c.defaultRatio),
  roleDefaultRatio: nn(c.roleDefaultRatio),
  companyName: nn(c.companyName),
  title: nn(c.title),
  firstName: nn(c.firstName),
  lastName: nn(c.lastName),
  name: nn(c.name),
  email: nn(c.email),
});

// Client → the pname/type echo set.
export const redRepClient = (c: any) => ({
  id: nn(c.id),
  type: nn(c.type),
  companyName: nn(c.companyName),
  title: nn(c.title),
  firstName: nn(c.firstName),
  lastName: nn(c.lastName),
  name: nn(c.name),
});

// Invoice → the byCase/byClient proforma sums + the Invoices-tab row fields.
export const redRepInv = (i: any) => ({
  caseId: nn(i.caseId),
  ccId: nn(i.ccId),
  type: nn(i.type),
  totalToPay: nn(i.totalToPay),
  payments: (i.payments || []).map((p: any) => ({ amount: nn(p.amount) })),
  // P17.1c — forward credits so computeReports can net outstanding/remaining
  // (it was the missing term: the server's credit arm saw creditsTotal=0).
  // Guard: credits may be absent or a scalar on some rows.
  credits: Array.isArray(i.credits) ? i.credits.map((c: any) => ({ amount: nn(c.amount) })) : [],
  // 216 addendum 7 §11 (4) [RULED 83] — the served amount due (shapes.js, the
  // P17.1c authority), so the Reports' `remaining` reads it (reportsAggregates.js)
  // instead of re-deriving it locally; absent on a row that never carried one.
  amountDue: nn((i as { amountDue?: number | null }).amountDue),
  number: nn(i.number),
  invoiceDate: nn(i.invoiceDate),
  caseRef: nn(i.caseRef),
  clientName: nn(i.clientName),
  // 213B §11 [RULED 44] — the ONE customer for the reports' client column: the first ticked client's name where the
  // pro forma is addressed to several (the snapshot's `addressees`), else absent (the server falls back to clientName)
  ...customerNameField(i),
  feeTotal: nn(i.feeTotal),
  paymentStatus: nn(i.paymentStatus),
  // 180 §5 (ruling 2) — "invoiced" in Reports means SENT NOW, on either channel.
  // `billitSubmittedAt` stood here: no live path ever stamped it (179 C.3), so
  // the report's "Envoyées" filter matched nothing. The two channel stamps are
  // what a send writes and what uncancel clears — the server's own `pfIsSent()`
  // in routes/invoices.js is exactly this disjunction.
  // Typed reads (the eslint ratchet counts an `any` read twice — member access
  // and assignment — and `nn()` returns `any`); `?? null` is nn()'s contract:
  // undefined → null, null → null, a stamp → itself.
  sentToClientAt: (i as { sentToClientAt?: string | null }).sentToClientAt ?? null,
  sentToPeppolAt: (i as { sentToPeppolAt?: string | null }).sentToPeppolAt ?? null,
  ...currencyOf(i),
});

// --- C.2 (time-entry fees) — POST /api/financials/entry-fees reducers -------
// (recording: recordings/c2-entryfees/; server consumer:
// server/src/lib/entryFees.js). Same wire discipline as above with ONE
// deliberate exception: `min` is passed through RAW (never nn'd) — the
// drawer/export arithmetic distinguishes a null min (divides to 0) from an
// ABSENT one (NaN → wire null in the export cells), and JSON.stringify
// dropping undefined keys reproduces "absent" exactly.

// Case / CollectiveCase → the effR read-set only (+ the costs ratio since
// 190 round 5 [O]: the per-row TTC the strip asks for under `table.ttc`,
// read only under that opt-in server-side). The row is typed at the seam
// (185's method) — a matter's shape as this reduction reads it.
interface EFMatterRow {
  id?: number | null;
  referenceHourlyRate?: number | null;
  costsRatio?: number | null;
  caseCollaborators?: readonly { coId?: number | null; ratio?: number | null }[] | null;
}
export const redEFMatter = (c: EFMatterRow) => ({
  id: nn(c.id),
  referenceHourlyRate: nn(c.referenceHourlyRate),
  costsRatio: nn(c.costsRatio),
  caseCollaborators: (c.caseCollaborators ?? []).map((x) => ({
    coId: nn(x.coId),
    ratio: nn(x.ratio),
  })),
});

// Collaborator → the effR chain read-set (no internalRates — no labor cost
// in this family).
export const redEFCo = (c: any) => ({
  id: nn(c.id),
  role: nn(c.role),
  billsAtZero: nn(c.billsAtZero),
  defaultRatio: nn(c.defaultRatio),
  roleDefaultRatio: nn(c.roleDefaultRatio),
});

// TimeEntry → per-surface row read-sets. `mi` indexes table.matters (null =
// row resolves to no matter), computed at the call site.
export const redEFTableRow = (e: any, mi: any) => ({
  id: nn(e.id),
  mi,
  coId: nn(e.coId),
  min: e.min,
  ...frz(e),
});
export const redEFDrawerRow = (e: any) => ({
  id: nn(e.id),
  caId: nn(e.caId),
  ccId: nn(e.ccId),
  coId: nn(e.coId),
  min: e.min,
  caseTitle: nn(e.caseTitle),
  caseRef: nn(e.caseRef),
  ...frz(e),
});
export const redEFExportRow = (e: any) => ({
  caId: nn(e.caId),
  ccId: nn(e.ccId),
  coId: nn(e.coId),
  min: e.min,
  ...frz(e),
});
export const redEFInvRow = (e: any) => ({
  caId: nn(e.caId),
  ccId: nn(e.ccId),
  coId: nn(e.coId),
  min: e.min,
  mark: nn(e.mark),
  billabilityMark: nn(e.billabilityMark),
  ...frz(e),
});

// --- C.2 (costs overview) — POST /api/financials/costs reducers -------------
// (recording: recordings/c2-costs/; server consumer:
// server/src/lib/costsOverview.js). Same wire discipline as above. The
// builders ECHO input fields into their rows (ref/title/status/ctype, the
// partner name/initials, the reduced matter as caseObj and the reduced
// collaborator as partnerCo/collab), so the reducers carry the echo set
// alongside the compute read-set; the view overlays the rich local objects
// back by id for badges/nav.

// A stamped __allCases row ({...case, _kind, _kid, _kref}) → the posted
// matter (the filter-selected subset travels in client order — row order
// and float summation order follow it).
export const redCostsMatter = (c: any) => ({
  _kind: nn(c._kind),
  _kid: nn(c._kid),
  _kref: nn(c._kref),
  title: nn(c.title),
  ctype: nn(c.ctype),
  status: nn(c.status),
  costsRatio: nn(c.costsRatio),
  referenceHourlyRate: nn(c.referenceHourlyRate),
  responsiblePartners: nn(c.responsiblePartners),
  caseCollaborators: (c.caseCollaborators || []).map((x: any) => ({
    coId: nn(x.coId),
    ratio: nn(x.ratio),
  })),
  // 190 round 9 [AW] — a payment's currency rides too: the Costs row's `paid` is recapBlock's,
  // split home/foreign as caseBlock splits it (169 R7 — a provision has none and is home).
  // 198 §A.3 — AND THE DOCUMENT IT ANSWERS: `invoiceId` (the served matter payment carries it,
  // lib/paymentRows.js matterPaymentsByMatter) is how « TAUX HORAIRE EFFECTIF »'s numerator
  // finds the payment's own ex-VAT / VAT split among the posted `matterInvoices` — never a
  // flat divisor. Absent on a frozen input → the compute answers null for that row, never 0.
  payments: (c.payments || []).map((p: { amount?: number | null; date?: string | null; invoiceId?: number | null }) => ({
    amount: nn(p.amount), date: nn(p.date), invoiceId: nn(p.invoiceId), ...currencyOf(p),
  })),
});

// TimeEntry → both matter keys + the money fields + the billability mark +
// the sec-table task type (posted in te-array order).
export const redCostsEntry = (e: any) => ({
  caId: nn(e.caId),
  ccId: nn(e.ccId),
  coId: nn(e.coId),
  min: nn(e.min),
  billed: nn(e.billed),
  date: nn(e.date),
  mark: nn(e.mark),
  type: nn(e.type),
  ...frz(e),
});

// Collaborator → the effR chain + internalRates (labor cost) + the
// fee-earner/sec classifiers + the pname/coIni echo set. isFeeEarner is
// LOAD-BEARING: isFeeEarnerCollab() consults it BEFORE the role-name
// fallback. (For non-admin viewers defaultRatio/internalRates arrive
// scrubbed from the API — posting them back yields the same zeros their
// browser used to compute.)
export const redCostsCo = (c: any) => ({
  id: nn(c.id),
  role: nn(c.role),
  isFeeEarner: nn(c.isFeeEarner),
  billsAtZero: nn(c.billsAtZero),
  defaultRatio: nn(c.defaultRatio),
  roleDefaultRatio: nn(c.roleDefaultRatio),
  internalRates: nn(c.internalRates),
  initials: nn(c.initials),
  companyName: nn(c.companyName),
  title: nn(c.title),
  firstName: nn(c.firstName),
  lastName: nn(c.lastName),
  name: nn(c.name),
});

/* ── 213B §11 [RULED 44] — THE SNAPSHOT'S ADDRESSEES, READ HERE ──
 * A pro forma addressed to several clients stores them in `clientAddressSnapshot.addressees` (server/src/lib/
 * invoiceAddressees.js normalizes the shape). This module reads them for the reports' reduced row — and holds the
 * reader for src/lib/proFormaAddressees.ts too, because recordings/reports-wire transpiles THIS file alone as a
 * data: URL: an import here is an import that cannot resolve there. */
export interface Addressee {
  clientId: number;
  name: string;
  email: string;
}

/** The stored addressees of a saved pro forma's snapshot, read through `unknown` (none on a pro forma made before 213B). */
export function storedAddressees(snapshot: unknown): Addressee[] {
  const raw = (snapshot as { addressees?: unknown } | null | undefined)?.addressees;
  const list: unknown[] = Array.isArray(raw) ? raw : [];
  return list
    .map((a) => {
      const r = a as { clientId?: unknown; name?: unknown; email?: unknown } | null | undefined;
      return { clientId: typeof r?.clientId === "number" ? r.clientId : 0, name: typeof r?.name === "string" ? r.name : "", email: typeof r?.email === "string" ? r.email : "" };
    })
    .filter((a) => a.clientId > 0);
}

/** The first ticked client's name on a saved pro forma addressed to several, else null. */
export function firstAddresseeNameOf(invoice: unknown): string | null {
  const snap = (invoice as { clientAddressSnapshot?: unknown } | null | undefined)?.clientAddressSnapshot;
  const name = storedAddressees(snap)[0]?.name ?? "";
  return name === "" ? null : name;
}

/** For the reports' reduced invoice row (financialsClient.ts `redRepInv`): `{ customerName }` where the pro forma is
 *  addressed to several — the ONE customer — else nothing (the server falls back to `clientName`). */
export function customerNameField(invoice: unknown): { customerName?: string } {
  const name = firstAddresseeNameOf(invoice);
  return name ? { customerName: name } : {};
}
